import * as BookmarkTypes from 'common/types/reports/bookmark';
import {handleListReorder} from 'common/components/draggable-list/utils';

import {getMetricAtIndex, updateMetricOrReferencedMetric} from '../helpers';
import {BookmarkDragOperationProps} from './types';
import {addFilterToTargetBehavior, generateFilter, getBehaviorForDragDropOperation} from './utils';

/**
 * Handles moving filters between events inside a metric.
 */
export function handleFilterAcrossMetricEvents(props: BookmarkDragOperationProps) {
  props.updateParams((params) => {
    const targetClause = getMetricAtIndex({
      index: props.targetData.metricIndex!,
      parentMetricIndex: props.targetData.parentMetricIndex,
      params,
      type: BookmarkTypes.TopLevelMetricType.Metric,
    });

    const sourceClause = getMetricAtIndex({
      index: props.sourceData.metricIndex!,
      parentMetricIndex: props.sourceData.parentMetricIndex,
      params,
      type: BookmarkTypes.TopLevelMetricType.Metric,
    });

    if (!sourceClause || !sourceClause.behavior?.behaviors || !targetClause || !targetClause.behavior?.behaviors) {
      throw new Error(`Invalid target clause for filter drop: ${props.targetData.group}`);
    }

    const sourceBehavior = getBehaviorForDragDropOperation(sourceClause, props.sourceData)!;
    const sourceFilter = sourceBehavior?.filters?.[props.sourceData.index];

    if (sourceFilter) {
      // makes sure all filters contain the determiner field
      addFilterToTargetBehavior(getBehaviorForDragDropOperation(targetClause, props.targetData), sourceFilter);

      // remove filter from source
      if (!props.isCloning) {
        sourceBehavior.filters?.splice(props.sourceData.index, 1);
      }

      // props.updateParams((params) => {
      updateMetricOrReferencedMetric({
        index: props.targetData.metricIndex!,
        parentMetricIndex: props.targetData.parentMetricIndex,
        params,
        newClause: targetClause,
      });
    }
  });
}

/**
 * Handles dropping a filter from the global filter section onto an event inside a metric.
 */
export function handleGlobalFilerToMetricEvent(props: BookmarkDragOperationProps) {
  props.updateParams((params) => {
    const targetClause = getMetricAtIndex({
      index: props.targetData.metricIndex!,
      parentMetricIndex: props.targetData.parentMetricIndex,
      params,
      type: BookmarkTypes.TopLevelMetricType.Metric,
    });

    if (!targetClause) {
      throw new Error(`Invalid target clause for filter drop: ${props.targetData.group}`);
    }

    const sourceFilter = generateFilter(props.params.sections.filter![props.sourceData.index], {
      isCloning: props.isCloning,
    });

    if (sourceFilter) {
      // makes sure all filters contain the determiner field
      addFilterToTargetBehavior(getBehaviorForDragDropOperation(targetClause, props.targetData), sourceFilter.result);

      updateMetricOrReferencedMetric({
        index: props.targetData.metricIndex!,
        parentMetricIndex: props.targetData.parentMetricIndex,
        params,
        newClause: targetClause,
      });

      // removes filter from source section
      if (!sourceFilter.forceClone && !props.isCloning) {
        params.sections.filter?.splice(props.sourceData.index, 1);
      }
    }
  });
}

/**
 * Handles rearranging events in a metric when dragging and dropping within a metric.
 */
export function handleMetricEventsRearrange(props: BookmarkDragOperationProps) {
  props.updateParams((params) => {
    const targetClause = getMetricAtIndex({
      params,
      index: props.targetData.metricIndex!,
      parentMetricIndex: props.targetData.parentMetricIndex,
      type: BookmarkTypes.TopLevelMetricType.Metric,
    });

    if (!targetClause || !targetClause?.behavior?.behaviors) {
      return;
    }

    // rearrange metric sub-behaviors
    handleListReorder({
      event: props.event,
      entries: targetClause.behavior.behaviors!,

      setEntries: (updatedEntries) => {
        // updates the draft clause
        targetClause.behavior!.behaviors = updatedEntries;

        updateMetricOrReferencedMetric({
          index: props.targetData.metricIndex!,
          parentMetricIndex: props.targetData.parentMetricIndex,
          params,
          newClause: targetClause,
        });
      },
    });
  });
}

/**
 * Handles rearranging filters within an event in a metric
 */
export function handleMetricEventFiltersRearrange(props: BookmarkDragOperationProps) {
  props.updateParams((params) => {
    const targetClause = getMetricAtIndex({
      params,
      index: props.targetData.metricIndex!,
      parentMetricIndex: props.targetData.parentMetricIndex,
      type: BookmarkTypes.TopLevelMetricType.Metric,
    });

    const behavior = getBehaviorForDragDropOperation(targetClause!, props.targetData);

    if (!behavior || !behavior.filters) {
      return;
    }

    handleListReorder({
      event: props.event,
      entries: behavior.filters,
      setEntries: (updatedEntries) => {
        // makes sure all filters contain the determiner field
        behavior.filters = updatedEntries;
        addFilterToTargetBehavior(behavior, null);

        // props.updateParams((params) =>
        updateMetricOrReferencedMetric({
          index: props.targetData.metricIndex!,
          parentMetricIndex: props.targetData.parentMetricIndex,
          params,
          newClause: targetClause!,
        });
      },
    });
  });
}
